import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authenticatedFetch } from '@/utils/authenticatedApiClient.js';
import { adminFetch } from '@/utils/adminApiClient.js';
import fallbackFeatureFlags from '@/config/featureFlags.ts';
import { useAuth } from '@/context/AuthContext.jsx';

const IS_TEST_MODE = import.meta.env.MODE === 'test';

const buildFallbackState = () =>
  fallbackFeatureFlags.map((flag) => ({
    ...flag,
    isEnabled: Boolean(flag.isEnabled),
    rolloutPercentage: flag.rolloutPercentage || 0,
    visibilityRules: flag.visibilityRules || {},
    metadata: flag.metadata || {},
    isFallback: true,
  }));

const FeatureFlagContext = createContext({
  flags: buildFallbackState(),
  isLoading: false,
  error: null,
  isAdmin: false,
  refresh: async () => {},
  toggleFlag: async () => {},
  updateFlag: async () => {},
  isFlagActive: () => false,
  getFlag: () => null,
});

FeatureFlagContext.displayName = 'FeatureFlagContext';

const mapApiFlag = (flag) => ({
  ...flag,
  isEnabled: Boolean(flag.isEnabled),
  rolloutPercentage: flag.rolloutPercentage || 0,
  visibilityRules: flag.visibilityRules || {},
  metadata: flag.metadata || {},
  assigned: Boolean(flag.assigned),
});

export const FeatureFlagProvider = ({ children }) => {
  const { user, isLoading: isAuthLoading, permissions: authPermissions } = useAuth();
  const [state, setState] = useState({
    flags: buildFallbackState(),
    isLoading: true,
    error: null,
    isAdmin: false,
    assignedFlagIds: [],
  });

  const refresh = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await authenticatedFetch('/api/feature-flags', {
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setState({
            flags: buildFallbackState(),
            isLoading: false,
            error: null,
            isAdmin: false,
            assignedFlagIds: [],
          });
          return;
        }

        const payload = await response.json().catch(() => null);
        const message = payload?.message || payload?.error || 'Impossibile caricare i feature flag.';
        throw new Error(message);
      }

      const payload = await response.json();
      if (!Array.isArray(payload?.flags)) {
        throw new Error('feature_flags_invalid_payload');
      }

      const flags = payload.flags.map(mapApiFlag);
      const permissionAdmin =
        Array.isArray(authPermissions) && authPermissions.includes('feature_flags.manage');
      const isAdmin = Boolean(payload?.context?.isAdmin || permissionAdmin);
      const assignedFlagIds = Array.isArray(payload?.context?.assignedFlagIds)
        ? payload.context.assignedFlagIds
        : [];

      setState({
        flags,
        isLoading: false,
        error: null,
        isAdmin,
        assignedFlagIds,
      });
    } catch (error) {
      if (!IS_TEST_MODE) {
        console.warn('[FeatureFlagProvider] Falling back to static feature flags', error);
      }
      setState({
        flags: buildFallbackState(),
        isLoading: false,
        error,
        isAdmin: Array.isArray(authPermissions)
          ? authPermissions.includes('feature_flags.manage')
          : false,
        assignedFlagIds: [],
      });
    }
  }, [authPermissions]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!user?.id) {
      setState((prev) => ({
        ...prev,
        flags: buildFallbackState(),
        isLoading: false,
        error: null,
        isAdmin: Array.isArray(authPermissions)
          ? authPermissions.includes('feature_flags.manage')
          : false,
        assignedFlagIds: [],
      }));
      return;
    }

    refresh();
  }, [isAuthLoading, user?.id, authPermissions, refresh]);

  const getFlag = useCallback(
    (key) => state.flags.find((flag) => flag.key === key) || null,
    [state.flags],
  );

  const isFlagActive = useCallback(
    (key) => {
      const flag = getFlag(key);
      if (!flag) {
        return false;
      }
      if (flag.isEnabled) {
        return true;
      }
      if (flag.assigned) {
        return true;
      }
      if (flag.id && state.assignedFlagIds.includes(flag.id)) {
        return true;
      }
      return false;
    },
    [getFlag, state.assignedFlagIds],
  );

  const applyFlagUpdate = useCallback((updatedFlag) => {
    setState((prev) => ({
      ...prev,
      flags: prev.flags.map((flag) => (flag.key === updatedFlag.key ? mapApiFlag(updatedFlag) : flag)),
    }));
  }, []);

  const toggleFlag = useCallback(
    async (key, isEnabled) => {
      if (!state.isAdmin) {
        throw new Error('not_authorized');
      }

      const response = await adminFetch(`/api/feature-flags/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ isEnabled }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.message || payload?.error || 'Impossibile aggiornare il feature flag.';
        throw new Error(message);
      }

      const payload = await response.json();
      if (payload?.flag) {
        applyFlagUpdate(payload.flag);
      } else {
        await refresh();
      }
    },
    [state.isAdmin, applyFlagUpdate, refresh],
  );

  const updateFlag = useCallback(
    async (key, updates) => {
      if (!state.isAdmin) {
        throw new Error('not_authorized');
      }

      const response = await adminFetch(`/api/feature-flags/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.message || payload?.error || 'Impossibile aggiornare il feature flag.';
        throw new Error(message);
      }

      const payload = await response.json();
      if (payload?.flag) {
        applyFlagUpdate(payload.flag);
      } else {
        await refresh();
      }
    },
    [state.isAdmin, applyFlagUpdate, refresh],
  );

  const value = useMemo(
    () => ({
      flags: state.flags,
      isLoading: state.isLoading,
      error: state.error,
      isAdmin: state.isAdmin,
      assignedFlagIds: state.assignedFlagIds,
      refresh,
      toggleFlag,
      updateFlag,
      isFlagActive,
      getFlag,
    }),
    [state, refresh, toggleFlag, updateFlag, isFlagActive, getFlag],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

export default FeatureFlagContext;
